import { LineChart } from "echarts/charts";
import {
  GridComponent,
  TooltipComponent,
  TitleComponent,
  DatasetComponent,
  LegendComponent,
  DataZoomComponent,
  AriaComponent,
  TimelineComponent,
} from "echarts/components";
import { SVGRenderer } from "echarts/renderers";
import { UniversalTransition, LabelLayout } from "echarts/features";
import * as echarts from "echarts/core";
import ReactEChartsCore from "echarts-for-react/lib/core";
import React, { useEffect, useRef, useState } from "react";
import { showToast } from "components/Toast/toast.slice";
import store from "app/store";
import getSprintReport from "services/api/GetSprintReportsApi";

interface SprintGraphData {
  xAxis: string[];
  yaxisTotalCount: any[];
  yaxisOpenCount: any[];
  yaxisClosedCount: any[];
  yaxisInProgressCount: any[];
}

interface SprintGraphValue {
  week: string[];
  total: number[];
  open: number[];
  closed: number[];
  inProgress: number[];
}

const LineSprintGraph: any = (props: any) => {
  const { projectName, startDate, endDate, graphType } = props;
  const [sprintGraphData, setSprintGraphData] = useState<SprintGraphData>({
    xAxis: [
      "week1",
      "week2",
      "week3",
      "week4",
      "week5",
      "week6",
      "week7",
      "week8",
    ],
    yaxisTotalCount: [20, 30, 40, 50, 30, 30, 60, 20],
    yaxisOpenCount: [10, 15, 20, 25, 15, 15, 30, 10],
    yaxisClosedCount: [5, 10, 15, 20, 10, 10, 20, 5],
    yaxisInProgressCount: [5, 5, 5, 5, 5, 5, 10, 5],
  });

  const setOptionsSprint = () => {
    return {
      baseOption: {
        aria: {
          enabled: true,
          label: {
            enabled: true,
          },
        },
        tooltip: {
          trigger: "item",
          position: "top",
          backgroundColor: "#000000",
          textStyle: {
            color: "#FFFFFF",
            fontSize: 10,
          },
          axisPointer: {
            type: "cross",
          },
          extraCssText: "z-index: 1991 !important;",
        },
        legend: {
          orient: "horizontal",
          x: "center",
          y: "bottom",
          icon: "circle",
          textStyle: {
            fontFamily: "Montserrat",
            fontWeight: 500,
            color: "#222328",
            fontSize: 14,
          },
          itemGap: 20,
          lineHeight: 18,
          itemHeight: 9,
          itemWidth: 9,
          padding: 10,
        },
        grid: {
          top: "5%",
          left: "0%",
          right: "9%",
          bottom: "20%",
          containLabel: true,
        },
        xAxis: {
          data: sprintGraphData.xAxis,
          type: "category",
          splitLine: {
            lineStyle: {
              width: 1,
              opacity: 0.5,
              color: "rgba(223, 223, 230, 1)",
            },
            show: true,
          },
          nameTextStyle: {
            fontFamily: " Montserrat",
            fontSize: 18,
            fontWeight: 600,
            lineHeight: 27,
            color: "#222328",
            align: "center",
            verticalAlign: "middle",
            overflow: "truncate",
            ellipsis: "...",
          },
          axisLabel: {
            formatter: (value: any) => {
              const newDates: any = `${value}`;
              return newDates;
            },
          },
          boundaryGap: false,
          axisLine: {
            show: false,
          },
          axisTick: {
            show: false,
          },
        },
        yAxis: {
          type: "value",
          axisLabel: {
            formatter(a: any) {
              a = +a;
              return Number.isFinite(a) ? echarts.format.addCommas(+a * 1) : "";
            },
          },
        },
        dataZoom: [
          {
            type: "inside",
            start: 0,
            end: 100,
          },
        ],
        textStyle: {
          fontFamily: " Montserrat",
          fontSize: 14,
          fontWeight: 600,
          lineHeight: 17,
          color: "#222328",
          align: "center",
          verticalAlign: "middle",
        },
        series: [
          {
            name: "Total Sprint",
            type: "line",
            smooth: true,
            data: sprintGraphData.yaxisTotalCount,
            itemStyle: {
              color: "#707070",
            },
            emphasis: {
              focus: "self",
              // blurScope: "coordinateSystem",
            },
            lineStyle: {
              width: 2,
              type: "solid",
              cap: "round",
            },
          },
          {
            name: "Total Closed",
            type: "line",
            smooth: true,
            data: sprintGraphData.yaxisClosedCount,
            emphasis: {
              focus: "solid",
              // blurScope: "coordinateSystem",
            },
            itemStyle: {
              color: "#FCC503",
            },
            lineStyle: {
              width: 2,
              type: "solid",
              cap: "round",
            },
          },
          {
            name: "Total Open",
            type: "line",
            smooth: true,
            data: sprintGraphData.yaxisOpenCount,
            emphasis: {
              focus: "self",
              // blurScope: "coordinateSystem",
            },
            itemStyle: {
              color: "#01A9A3",
            },
            lineStyle: {
              width: 2,
              type: "solid",
              cap: "round",
            },
          },
          {
            name: "Total Pending",
            type: "line",
            smooth: true,
            data: sprintGraphData.yaxisInProgressCount,
            emphasis: {
              focus: "self",
              // blurScope: "coordinateSystem",
            },
            itemStyle: {
              color: "#26ad7c",
            },
            lineStyle: {
              width: 2,
              type: "solid",
              cap: "round",
            },
          },
        ],
      },
    };
  };

  // COMMON CODE
  const [selectedWeeks, setSelectedWeeks] = useState<number>(4);
  const [loading, setLoading] = useState(true);
  const getCurrentEchartInstance = (chart: any) => {
    return chart.getEchartsInstance();
  };

  const chartDom3 = useRef(null);
  let chartInstance3: any = null;
  // Unforcasted Automation Graph
  async function renderChart2(chart: any) {
    const renderInstance = getCurrentEchartInstance(chart);
    if (renderInstance) {
      chartInstance3 = renderInstance;
    } else {
      chartInstance3 = echarts.init(chart);
    }
    if (
      sprintGraphData.yaxisClosedCount.reduce(
        (a: number, b: number) => a + b,
        0
      ) === 0 &&
      sprintGraphData.yaxisInProgressCount.reduce(
        (a: any, b: any) => a + b,
        0
      ) === 0 &&
      sprintGraphData.yaxisOpenCount.reduce((a: any, b: any) => a + b, 0) ===
        0 &&
      sprintGraphData.yaxisTotalCount.reduce((a: any, b: any) => a + b, 0) === 0
    ) {
      renderInstance.setOption({
        title: {
          show: true,
          textStyle: {
            color: "grey",
            fontSize: 20,
          },
          text: "No data",
          left: "center",
          top: "center",
        },
      });
    } else {
      await chartInstance3.setOption({
        baseOption: {
          title: {
            show: false,
          },
          xAxis: {
            data: sprintGraphData.xAxis,
          },
          series: [
            {
              name: "Total Sprint",
              data: sprintGraphData.yaxisTotalCount,
            },
            {
              name: "Total Closed",
              data: sprintGraphData.yaxisClosedCount,
            },
            {
              name: "Total Open",
              data: sprintGraphData.yaxisOpenCount,
            },
            {
              name: "Total Pending",
              data: sprintGraphData.yaxisInProgressCount,
            },
          ],
        },
        // All EChart Supported Media Queries are relative to the size of the charts and not the current window size
        media: [
          {
            option: {
              series: [
                {
                  name: "Total Sprint",
                  emphasis: {
                    disabled: false,
                    focus: "self",
                  },
                },
                {
                  name: "Total Closed",
                  emphasis: {
                    disabled: false,
                    focus: "self",
                  },
                },
                {
                  name: "Total Open",
                  emphasis: {
                    disabled: false,
                    focus: "self",
                  },
                },
                {
                  name: "Total Pending",
                  emphasis: {
                    disabled: false,
                    focus: "self",
                  },
                },
              ],
            },
          },
          {
            query: {
              maxWidth: 767,
            },
            option: {
              series: [
                {
                  name: "Total Sprint",
                  emphasis: {
                    disabled: true,
                  },
                },
                {
                  name: "Total Closed",
                  emphasis: {
                    disabled: true,
                  },
                },
                {
                  name: "Total Open",
                  emphasis: {
                    disabled: true,
                  },
                },
                {
                  name: "Total Pending",
                  emphasis: {
                    disabled: true,
                  },
                },
              ],
            },
          },
        ],
      });

      await chartInstance3.setOption({
        xAxis: {
          axisLabel: {
            formatter: (value: any) => {
              const newDates: any = new Date(`${value}`).toLocaleDateString(
                "en-US",
                {
                  day: "2-digit",
                  month: "short",
                }
              );
              return newDates;
            },
          },
        },
      });
    }
    setLoading(false);
  }
  const GetSprintApi = async () => {
    getSprintReport(projectName, startDate, endDate)
      .then(async (response: any) => {
        const { qaReports } = response.data;
        let xaxis: any[] = [];
        let yaxisTotal: any[] = [];
        let yaxisClosed: any[] = [];
        let yaxisOpen: any[] = [];
        let yaxisInProgress: any[] = [];
        qaReports.forEach((val: SprintGraphValue) => {
          const newDates: any = new Date(`${val.week}`); // Change the date format as per your requirement
          xaxis = [...xaxis, newDates];
          yaxisTotal = [...yaxisTotal, val.total];
          yaxisClosed = [...yaxisClosed, val.closed];
          yaxisOpen = [...yaxisOpen, val.open];
          yaxisInProgress = [...yaxisInProgress, val.inProgress];
        });
        setSprintGraphData({
          ...sprintGraphData,
          xAxis: xaxis,
          yaxisTotalCount: yaxisTotal,
          yaxisClosedCount: yaxisClosed,
          yaxisOpenCount: yaxisOpen,
          yaxisInProgressCount: yaxisInProgress,
        });
        setLoading(false);
      })
      .catch((error: any) => {
        setLoading(false);
        store.dispatch(
          showToast({
            type: "danger",
            title: "Error Occurred",
            message: error.message,
          })
        );
      });
  };
  // Use Effect to monitor and update the graph on change in dropdown
  useEffect(() => {
    // Axios request to hit endpoint
    setLoading(true);
    GetSprintApi();
  }, [startDate, endDate, projectName, graphType]);

  // Forcasted Automation Graph
  useEffect(() => {
    // Update chart values

    if (chartDom3.current !== null) {
      const lineChart = chartDom3.current;
      renderChart2(lineChart);
    }
    document
      .querySelector<any>(".echarts-for-react")
      ?.setAttribute("role", "img");
  }, [sprintGraphData]);

  useEffect(() => {
    return () => {
      if (chartDom3.current !== null) {
        chartInstance3 = getCurrentEchartInstance(chartDom3.current);
      }
    };
  }, []);

  // Loading elements
  const loadingOption = {
    text: "loading",
    color: "#c23531",
    textColor: "#000",
    maskColor: "rgba(255, 255, 255, 0.8)",
    zlevel: 0,
    fontSize: 12,
    showSpinner: true,
    spinnerRadius: 10,
    lineWidth: 5,
    fontWeight: "normal",
    fontStyle: "normal",
  };

  useEffect(() => {
    // Update chart
    if (chartDom3.current !== null) {
      const chart = getCurrentEchartInstance(chartDom3.current);
    }
  }, [loading]);

  // Register the required components
  echarts.use([
    // CanvasRenderer,
    SVGRenderer,
    GridComponent,
    TooltipComponent,
    TitleComponent,
    DatasetComponent,
    LegendComponent,
    LineChart,
    UniversalTransition,
    DataZoomComponent,
    LabelLayout,
    AriaComponent,
    TimelineComponent,
  ]);
  return (
    <div>
      <ReactEChartsCore
        echarts={echarts}
        ref={chartDom3}
        option={setOptionsSprint()}
        className="echart-wrapper transaction-log px-3 mx-2"
        loadingOption={loadingOption}
        showLoading={loading}
        opts={{ renderer: "svg" }}
        lazyUpdate
      />
    </div>
  );
};

export default LineSprintGraph;
